import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, Pressable } from 'react-native';
import { router } from 'expo-router';
import { Building2, FileText, Search, Users } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  DESKTOP_CONTENT_MAX_WIDTH,
  CONTENT_MAX_WIDTH,
} from '../../src/theme';
import { Header } from '@components/layout';
import {
  Button,
  EmptyState,
  ErrorBanner,
  Input,
  Table,
  type TableColumn,
  type TableSort,
} from '@components/common';
import { SendReportSheet } from '../../src/components/agent/SendReportSheet';
import { useAuthStore } from '../../src/store/authStore';
import { useAgentClientsStore } from '../../src/store/agentClientsStore';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { useWebHover } from '@hooks/useWebHover';
import { CURSOR_POINTER } from '@utils/webStyles';
import type { ClientStructure } from '../../src/types/api';
import {
  filterClients,
  sortClients,
  computeAgentStats,
  lastActivityMs,
  INACTIVE_AFTER_DAYS,
  type ClientSortKey,
} from '../../src/utils/agentStats';
import {
  getReadinessLevelFromClaim,
  getReadinessPercent,
  getReadinessColor,
  type ReadinessLevel,
} from '../../src/utils/readiness';
import { formatRelativeTimeFromMillis, formatRelativeTime } from '../../src/utils/time';

type BandFilter = ReadinessLevel | 'all';

const BAND_FILTERS: { key: BandFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'progress', label: 'On track' },
  { key: 'partial', label: 'In progress' },
  { key: 'risk', label: 'At risk' },
  { key: 'none', label: 'Not started' },
];

const INACTIVE_MS = INACTIVE_AFTER_DAYS * 24 * 60 * 60 * 1000;

export default function AgentClientsScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const isDesktopShell = useDesktopShell();
  const { user } = useAuthStore();

  const clients = useAgentClientsStore((s) => s.clients);
  const isLoading = useAgentClientsStore((s) => s.isLoading);
  const error = useAgentClientsStore((s) => s.error);
  const fetchClients = useAgentClientsStore((s) => s.fetchClients);

  const [search, setSearch] = useState('');
  const [band, setBand] = useState<BandFilter>('all');
  const [sort, setSort] = useState<TableSort>({ key: 'address', direction: 'asc' });
  const [reportClient, setReportClient] = useState<ClientStructure | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const insurerName = user?.agentProfile?.insurerName;

  useEffect(() => {
    if (insurerName) void fetchClients(insurerName);
  }, [insurerName, fetchClients]);

  const handleRefresh = async () => {
    if (!insurerName) return;
    setRefreshing(true);
    await fetchClients(insurerName, { force: true });
    setRefreshing(false);
  };

  const stats = useMemo(() => computeAgentStats(clients, Date.now()), [clients]);
  const hasReportHistory = useMemo(
    () => clients.some((c) => c.claim.lastReport != null),
    [clients]
  );

  const visible = useMemo(() => {
    const filtered = filterClients(clients, { search, band });
    return sortClients(filtered, sort.key as ClientSortKey, sort.direction);
  }, [clients, search, band, sort]);

  const columns = useMemo<TableColumn<ClientStructure>[]>(() => {
    const cols: TableColumn<ClientStructure>[] = [
      {
        key: 'address',
        header: 'Address',
        sortable: true,
        collapse: 'title',
        render: (c) => <AddressCell client={c} />,
      },
      {
        key: 'readiness',
        header: 'Readiness',
        width: 150,
        sortable: true,
        collapse: 'detail',
        render: (c) => <ReadinessCell client={c} />,
      },
      {
        key: 'checklist',
        header: 'Checklist',
        width: 120,
        sortable: true,
        collapse: 'detail',
        render: (c) => <ChecklistCell client={c} />,
      },
      {
        key: 'lastActivity',
        header: 'Last activity',
        width: 130,
        sortable: true,
        collapse: 'detail',
        render: (c) => <LastActivityCell client={c} />,
      },
    ];
    if (hasReportHistory) {
      cols.push({
        key: 'lastReport',
        header: 'Last report',
        width: 120,
        sortable: true,
        collapse: 'detail',
        render: (c) => <LastReportCell client={c} />,
      });
    }
    cols.push({
      key: 'actions',
      header: '',
      width: 150,
      align: 'right',
      collapse: 'detail',
      interactive: true,
      render: (c) => (
        <Button
          title="Send report"
          variant="outline"
          size="xs"
          leftIcon={FileText}
          onPress={() => setReportClient(c)}
          testID={`send-report-${c.structure.id}`}
        />
      ),
    });
    return cols;
  }, [hasReportHistory]);

  if (!user) return null;

  if (!user.agentProfile) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Client Properties" />
        <EmptyState
          icon={Users}
          title="Not an insurance agent"
          message="Your account doesn't have agent access. Contact your insurer admin for an invite."
        />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header
        showBack
        showAvatar={false}
        large
        wide
        title="Client Properties"
        // Desktop shell: the sidebar heads the agent section with this same
        // insurer name, so repeating it here is duplicated chrome — and the
        // second line pushed this header past SHELL_HEADER_HEIGHT, leaving its
        // rule out of line with the sidebar's. Mobile has no sidebar and keeps it.
        subtitle={isDesktopShell ? undefined : insurerName}
      />
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { maxWidth: isDesktopShell ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH },
          { paddingBottom: insets.bottom + 80 },
        ]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
        showsVerticalScrollIndicator={false}>
        <ErrorBanner message={error} />

        {/* Toolbar */}
        <View style={styles.toolbar}>
          <Input
            leftIcon={Search}
            placeholder="Search properties"
            value={search}
            onChangeText={setSearch}
            containerStyle={styles.searchInput}
            accessibilityLabel="Search properties"
          />
          <View style={styles.filterRow}>
            {BAND_FILTERS.map((f) => (
              <FilterChip
                key={f.key}
                label={
                  f.key === 'all'
                    ? `All (${stats.total})`
                    : `${f.label} (${stats.bandCounts[f.key as ReadinessLevel]})`
                }
                selected={band === f.key}
                onPress={() => setBand(f.key)}
              />
            ))}
            <View style={styles.spacer} />
            {visible.length !== clients.length && (
              <Text style={[styles.resultCount, { color: colors.textSecondary }]}>
                {visible.length} of {clients.length} properties
              </Text>
            )}
          </View>
        </View>

        <Table
          columns={columns}
          data={visible}
          keyExtractor={(c) => c.structure.id}
          sort={sort}
          onSortChange={setSort}
          onRowPress={(c) => router.push(`/structure/${c.structure.id}`)}
          loading={isLoading && clients.length === 0}
          testID="client-properties-table"
          emptyState={
            clients.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No client properties yet"
                message={`No structures are currently assigned to ${insurerName} lists.`}
              />
            ) : (
              <EmptyState
                icon={Search}
                title="No properties match"
                message="Try a different search or filter."
                actionLabel="Clear filters"
                onAction={() => {
                  setSearch('');
                  setBand('all');
                }}
              />
            )
          }
        />
      </ScrollView>

      <SendReportSheet client={reportClient} onClose={() => setReportClient(null)} />
    </View>
  );
}

function FilterChip({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      {...hoverProps}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      // RN Web drops accessibilityState, and role="radio" requires
      // aria-checked in the DOM (same as ClaimSheet and OptionsPanel).
      aria-checked={selected}
      // Hairline pill by default, ink pill when chosen — the same grammar as
      // OptionsPanel's visibility pills and the claim sheet's chips.
      style={[
        styles.chip,
        {
          borderColor: selected ? colors.action : colors.border,
          backgroundColor: selected ? colors.action : hovered ? colors.hoverSurface : 'transparent',
        },
        CURSOR_POINTER,
      ]}>
      <Text style={[styles.chipLabel, { color: selected ? colors.actionOn : colors.text }]}>
        {label}
      </Text>
    </Pressable>
  );
}

function AddressCell({ client }: { client: ClientStructure }) {
  const { colors } = useTheme();
  const s = client.structure;
  return (
    <View>
      <Text style={[styles.address, { color: colors.text }]} numberOfLines={1}>
        {s.address || 'Unknown address'}
      </Text>
      <Text style={[styles.city, { color: colors.textSecondary }]} numberOfLines={1}>
        {s.city}, {s.state} {s.zip ?? ''}
      </Text>
    </View>
  );
}

function ReadinessCell({ client }: { client: ClientStructure }) {
  const { colors } = useTheme();
  const percent = getReadinessPercent(client.claim);
  const band = getReadinessLevelFromClaim(client.claim);
  const color = getReadinessColor(band, colors) ?? colors.textSecondary;

  if (percent === null) {
    return (
      <Text testID="client-readiness" style={[styles.muted, { color: colors.textSecondary }]}>
        Not started
      </Text>
    );
  }

  // Flat fill in the level color. Same 4px track as ReadinessMeter, so the
  // app has one readiness grammar.
  return (
    <View testID="client-readiness" style={styles.readinessCell}>
      <Text style={[styles.percent, { color }]}>{percent}%</Text>
      <View style={[styles.progressTrack, { backgroundColor: colors.borderLight }]}>
        <View
          style={[styles.progressFill, { width: `${percent}%` as const, backgroundColor: color }]}
        />
      </View>
    </View>
  );
}

function ChecklistCell({ client }: { client: ClientStructure }) {
  const { colors } = useTheme();
  const snap = client.claim.progressSnapshot;
  if (!snap || snap.total === 0) {
    return (
      <Text testID="client-checklist" style={[styles.muted, { color: colors.textSecondary }]}>
        —
      </Text>
    );
  }
  return (
    <Text testID="client-checklist" style={[styles.cellText, { color: colors.text }]}>
      {snap.completed} of {snap.total}
    </Text>
  );
}

function LastActivityCell({ client }: { client: ClientStructure }) {
  const { colors } = useTheme();
  const ms = lastActivityMs(client);
  const label = formatRelativeTimeFromMillis(ms, { suffix: 'ago' });
  const stale = ms !== null && Date.now() - ms > INACTIVE_MS;
  if (!label)
    return (
      <Text testID="client-last-activity" style={[styles.muted, { color: colors.textSecondary }]}>
        —
      </Text>
    );
  return (
    <Text
      testID="client-last-activity"
      style={[styles.cellText, { color: stale ? colors.risk : colors.textSecondary }]}>
      {label}
    </Text>
  );
}

function LastReportCell({ client }: { client: ClientStructure }) {
  const { colors } = useTheme();
  const label = formatRelativeTime(client.claim.lastReport?.at, { suffix: 'ago' });
  return label ? (
    <Text testID="client-last-report" style={[styles.cellText, { color: colors.textSecondary }]}>
      {label}
    </Text>
  ) : (
    <Text testID="client-last-report" style={[styles.muted, { color: colors.textSecondary }]}>
      Never
    </Text>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: {
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
    gap: SPACING.sm,
  },
  toolbar: {
    gap: SPACING.sm,
  },
  searchInput: {
    marginBottom: 0,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  spacer: { flex: 1 },
  resultCount: {
    ...TYPOGRAPHY.bodySmall,
  },
  chip: {
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.xs,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  chipLabel: {
    ...TYPOGRAPHY.label,
  },
  address: {
    ...TYPOGRAPHY.button,
  },
  city: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  readinessCell: {
    gap: SPACING.xs,
  },
  percent: {
    ...TYPOGRAPHY.button,
    fontVariant: ['tabular-nums' as const],
  },
  progressTrack: {
    height: 6,
    borderRadius: BORDER_RADIUS.full,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: BORDER_RADIUS.full,
  },
  cellText: {
    ...TYPOGRAPHY.body,
  },
  muted: {
    ...TYPOGRAPHY.body,
  },
});
